import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Paper,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Pets as PetsIcon,
  Inventory as InventoryIcon,
  Today as TodayIcon,
  Warning as WarningIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Healing as HealingIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Vaccines as VaccinesIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import { patientsService } from '../../services/firebaseServices';
import { vacunasService, desparasitacionesService } from '../../services/medicinaPreventiva';
import { collection, getDocs, query, orderBy, getFirestore } from 'firebase/firestore';
import app from '../../config/firebase';
import dayjs from 'dayjs';

// Inicializacion de Firestore
const db = getFirestore(app);

// Funcion para calcular dias hasta vencimiento (igual que en Inventario.jsx)
const calcularDiasHastaVencimiento = (fechaVencimiento) => {
  if (!fechaVencimiento) return null;
  
  const hoy = new Date();
  const fechaVence = new Date(fechaVencimiento);
  const diferencia = fechaVence - hoy;
  return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
};

// Funcion auxiliar para formatear fechas de Firestore
const formatearFecha = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate().toLocaleDateString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString();
  }
  return null;
};

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    appointments: true,
    inventory: true,
    patients: true,
    recentPatients: true
  });
  const [error, setError] = useState({
    appointments: null,
    inventory: null,
    patients: null,
    recentPatients: null
  });
  
  // Estados para los datos
  const [citasHoy, setCitasHoy] = useState([]);
  const [citasProximas, setCitasProximas] = useState([]);
  const [citasPendientesHoy, setCitasPendientesHoy] = useState([]);
  const [citasCompletadas, setCitasCompletadas] = useState([]);
  const [, setUltimosPacientes] = useState([]);
  const [pacientesRecientes, setPacientesRecientes] = useState([]);
  const [, setInventarioBajo] = useState([]);
  const [inventarioProximoAVencer, setInventarioProximoAVencer] = useState([]);
  const [proximasMedicinaPreventiva, setProximasMedicinaPreventiva] = useState([]);
  const [, setDatosFinancieros] = useState({
    ingresosMes: 0,
    cargando: true
  });
  
  // Stats din+�micos basados en los datos reales
  const [stats, setStats] = useState([
    {
      title: 'Citas Hoy',
      value: '0',
      change: '0',
      color: 'primary',
      icon: <TodayIcon fontSize="large" />
    },
    {
      title: 'Pacientes Activos',
      value: '0',
      change: '0',
      color: 'success',
      icon: <PetsIcon fontSize="large" />
    },
    {
      title: 'Ingresos del Mes',
      value: '$0',
      change: 'Ver detalles',
      color: 'warning',
      icon: <MonetizationOnIcon fontSize="large" />
    }
  ]);
  
  // Cargar datos de citas
  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(prev => ({ ...prev, appointments: true }));
      try {
        const today = dayjs().format('YYYY-MM-DD');
        const now = dayjs();

        const citasRef = collection(db, 'citas');
        const q = query(citasRef, orderBy('fecha', 'asc'), orderBy('hora', 'asc'));
        const querySnapshot = await getDocs(q);
        const citas = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const normalizeDate = (fecha) => {
          if (!fecha) return null;

          if (typeof fecha === 'object' && typeof fecha.toDate === 'function') {
            return dayjs(fecha.toDate()).format('YYYY-MM-DD');
          }

          const fechaStr = String(fecha).trim();
          if (!fechaStr) return null;

          if (fechaStr.includes('/')) {
            const parts = fechaStr.split('/');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              const parsed = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD');
              return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
            }
          }

          const parsed = dayjs(fechaStr);
          return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
        };

        const normalizeHour = (hora) => {
          if (!hora) return null;

          const raw = String(hora).trim().toLowerCase();
          const match = raw.match(/(\d{1,2})[:.](\d{2})/);
          if (!match) return null;

          let h = parseInt(match[1], 10);
          const m = match[2];
          const hasPm = /(\bpm\b|p\.\s?m\.?)/i.test(raw);
          const hasAm = /(\bam\b|a\.\s?m\.?)/i.test(raw);

          if (hasPm && h < 12) h += 12;
          if (hasAm && h === 12) h = 0;

          if (h < 0 || h > 23) return null;
          return `${String(h).padStart(2, '0')}:${m}`;
        };

        const citasNormalizadas = citas.map(cita => {
          const fechaNormalizada = normalizeDate(cita.fecha);
          const horaNormalizada = normalizeHour(cita.hora24 || cita.horaNormalizada || cita.hora);
          return {
            ...cita,
            fechaNormalizada,
            horaNormalizada,
          };
        });

        const uniqueKeys = new Set();
        const citasUnicas = citasNormalizadas.filter(cita => {
          const clave = [
            cita.fechaNormalizada || '',
            cita.horaNormalizada || '',
            (cita.paciente || '').trim().toLowerCase(),
            (cita['due\u00f1o'] || cita.dueno || '').trim().toLowerCase(),
            (cita.tipo || '').trim().toLowerCase(),
          ].join('|');

          if (uniqueKeys.has(clave)) return false;
          uniqueKeys.add(clave);
          return true;
        });

        const getFechaHoraCita = (cita) => {
          if (!cita?.fechaNormalizada || !cita?.horaNormalizada) return null;
          const fechaHora = dayjs(`${cita.fechaNormalizada} ${cita.horaNormalizada}`, 'YYYY-MM-DD HH:mm');
          return fechaHora.isValid() ? fechaHora : null;
        };

        const citasHoyData = citasUnicas
          .filter(cita => cita.fechaNormalizada === today)
          .sort((a, b) => String(a.horaNormalizada || '').localeCompare(String(b.horaNormalizada || '')));

        const citasPendientesHoyData = citasHoyData.filter(cita => {
          const fechaHora = getFechaHoraCita(cita);
          return (
            !['completada', 'cancelada', 'no_asistio'].includes(cita.estado) &&
            fechaHora &&
            !fechaHora.isBefore(now)
          );
        });

        const citasPendientesPasadas = citasHoyData.filter(cita => {
          const fechaHora = getFechaHoraCita(cita);
          return (
            !['completada', 'cancelada', 'no_asistio'].includes(cita.estado) &&
            fechaHora &&
            fechaHora.isBefore(now)
          );
        });

        const citasPendientesFuturas = citasUnicas
          .filter(cita => !['completada', 'cancelada', 'no_asistio'].includes(cita.estado))
          .map(cita => {
            const fechaHora = getFechaHoraCita(cita);
            return {
              ...cita,
              _fechaHora: fechaHora,
              horaMostrar: fechaHora?.isValid() ? fechaHora.format('HH:mm') : (cita.horaNormalizada || String(cita.hora || '').trim())
            };
          })
          .filter(cita => cita._fechaHora && !cita._fechaHora.isBefore(now))
          .sort((a, b) => a._fechaHora.valueOf() - b._fechaHora.valueOf());

        // Evitar repetir el mismo paciente en diferentes horas: tomar solo su cita m�s cercana.
        const pacientesVistos = new Set();
        const citasPendientesPorPaciente = citasPendientesFuturas.filter(cita => {
          const clavePaciente = String(
            cita.pacienteId || `${cita.paciente || ''}|${cita['due\u00f1o'] || cita.dueno || ''}`
          )
            .trim()
            .toLowerCase();

          if (pacientesVistos.has(clavePaciente)) {
            return false;
          }

          pacientesVistos.add(clavePaciente);
          return true;
        });

        const citasCompletadasHoy = citasHoyData.filter(cita => cita.estado === 'completada');

        setStats(prevStats => {
          const newStats = [...prevStats];
          const totalPendientes = citasPendientesHoyData.length + citasPendientesPasadas.length;
          newStats[0] = {
            ...newStats[0],
            value: totalPendientes.toString(),
            change: `+${citasPendientesPorPaciente.length} proximas`
          };
          return newStats;
        });

        setCitasHoy(citasHoyData);
        setCitasPendientesHoy(citasPendientesPorPaciente);
        setCitasProximas(citasPendientesPorPaciente.slice(0, 5));
        setCitasCompletadas(citasCompletadasHoy);
        setUltimosPacientes(citasCompletadasHoy.slice(0, 5));
      } catch (err) {
        console.error("Error al cargar citas:", err);
        setError(prev => ({ ...prev, appointments: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, appointments: false }));
      }
    };
    
    fetchAppointments();
  }, []);
  
  // Cargar datos de inventario
  useEffect(() => {
    const fetchInventory = async () => {
      setLoading(prev => ({ ...prev, inventory: true }));
      try {
        // Usar el mismo m+�todo que el m+�dulo de inventario
        const inventarioRef = collection(db, 'inventario');
        const q = query(inventarioRef, orderBy('nombre'));
        const querySnapshot = await getDocs(q);
        
        const items = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`��� Inventario cargado: ${items.length} productos`);
        items.forEach(item => {
          if (item.fechaVencimiento) {
            console.log(`- ${item.nombre}: ${item.fechaVencimiento} (tipo: ${typeof item.fechaVencimiento})`);
          }
        });
        
        if (items.length > 0) {
          
          // Filtrar items con stock bajo
          const bajoStock = items.filter(item => 
            item.cantidad && item.stockMinimo && item.cantidad <= item.stockMinimo
          ).sort((a, b) => (a.cantidad / a.stockMinimo) - (b.cantidad / b.stockMinimo));
          
          // Filtrar items próximos a vencer (menos de 30 días) - usando la misma lógica que Inventario.jsx
          const proximosAVencer = items.filter(item => {
            if (!item.fechaVencimiento) return false;
            const diasRestantes = calcularDiasHastaVencimiento(item.fechaVencimiento);
            return diasRestantes <= 30; // Alertar para productos que vencen en menos de 30 días
          });
          
          console.log(`Productos próximos a vencer encontrados: ${proximosAVencer.length}`);
          proximosAVencer.forEach(item => {
            const diasRestantes = calcularDiasHastaVencimiento(item.fechaVencimiento);
            console.log(`- ${item.nombre}: ${item.fechaVencimiento} (${diasRestantes} días restantes)`);
          });
          
          // Guardar los primeros 5 elementos
          setInventarioBajo(bajoStock.slice(0, 5));
          setInventarioProximoAVencer(proximosAVencer.slice(0, 10)); // Mostrar hasta 10 productos
        }
      } catch (err) {
        console.error("Error al cargar inventario:", err);
        setError(prev => ({ ...prev, inventory: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, inventory: false }));
      }
    };
    
    fetchInventory();
  }, []);

  // Cargar pacientes recientes ordenados por +�ltima visita  
  useEffect(() => {
    const fetchRecentPatients = async () => {
      setLoading(prev => ({ ...prev, recentPatients: true }));
      try {
        const response = await patientsService.getAllPatients();
        
        if (response.success) {
          // Filtrar pacientes activos con +�ltima visita
          const patientesConVisitas = response.data.filter(paciente => 
            paciente.activo !== false && 
            (paciente.ultimaVisita || paciente.fechaCreacion)
          );

          // Ordenar por fecha de +�ltima visita (m+�s recientes primero)
          patientesConVisitas.sort((a, b) => {
            const fechaA = a.ultimaVisita?.toDate?.() || a.fechaCreacion?.toDate?.() || new Date(0);
            const fechaB = b.ultimaVisita?.toDate?.() || b.fechaCreacion?.toDate?.() || new Date(0);
            return fechaB - fechaA;
          });
          
          // Tomar los 3 m+�s recientes
          setPacientesRecientes(patientesConVisitas.slice(0, 3));
          
          // Actualizar estad+�sticas de pacientes activos
          setStats(prevStats => {
            const newStats = [...prevStats];
            newStats[1] = {
              ...newStats[1],
              value: response.data.filter(p => p.activo !== false).length.toString(),
              change: `+${patientesConVisitas.length} con visita reciente`
            };
            return newStats;
          });
        }
      } catch (err) {
        console.error("Error al cargar pacientes recientes:", err);
        setError(prev => ({ ...prev, recentPatients: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, recentPatients: false }));
      }
    };
    
    fetchRecentPatients();
  }, []);

  // Cargar datos de medicina preventiva pr+�xima
  useEffect(() => {
    const fetchProximasMedicinaPreventiva = async () => {
      try {
        console.log('���� Buscando pr+�ximas vacunas y desparasitaciones...');
        
        // Obtener todos los pacientes primero
        const pacientesResponse = await patientsService.getAllPatients();
        if (!pacientesResponse.success) {
          console.error('Error obteniendo pacientes:', pacientesResponse.error);
          return;
        }

        const pacientes = pacientesResponse.data;
        const proximasActividades = [];
        const hoy = dayjs();
        
        // Para cada paciente, obtener sus vacunas y desparasitaciones
        for (const paciente of pacientes) {
          try {
            // Obtener vacunas del paciente
            const vacunasResponse = await vacunasService.getVacunasByPaciente(paciente.id);
            if (vacunasResponse.success && vacunasResponse.data.length > 0) {
              // Buscar la vacuna m+�s reciente que tenga pr+�xima dosis
              const vacunaReciente = vacunasResponse.data
                .filter(v => v.proximaDosis)
                .sort((a, b) => dayjs(b.fechaAplicacion).diff(dayjs(a.fechaAplicacion)))
                [0];
              
              if (vacunaReciente && vacunaReciente.proximaDosis) {
                const fechaProximaDosis = dayjs(vacunaReciente.proximaDosis);
                const diasRestantes = fechaProximaDosis.diff(hoy, 'day');
                
                // Si está dentro de los próximos 30 días
                if (diasRestantes >= 0 && diasRestantes <= 30) {
                  proximasActividades.push({
                    tipo: 'vacuna',
                    paciente: {
                      id: paciente.id,
                      nombre: paciente.nombre,
                      especie: paciente.especie
                    },
                    actividad: vacunaReciente.nombreVacuna || 'Vacuna',
                    fechaProxima: vacunaReciente.proximaDosis,
                    diasRestantes: diasRestantes,
                    urgente: diasRestantes <= 7
                  });
                }
              }
            }

            // Obtener desparasitaciones del paciente
            const desparasitacionesResponse = await desparasitacionesService.getDesparasitacionesByPaciente(paciente.id);
            if (desparasitacionesResponse.success && desparasitacionesResponse.data.length > 0) {
              // Buscar la desparasitación más reciente que tenga próxima dosis
              const desparasitacionReciente = desparasitacionesResponse.data
                .filter(d => d.proximaDosis)
                .sort((a, b) => dayjs(b.fechaAplicacion).diff(dayjs(a.fechaAplicacion)))
                [0];
              
              if (desparasitacionReciente && desparasitacionReciente.proximaDosis) {
                const fechaProximaDosis = dayjs(desparasitacionReciente.proximaDosis);
                const diasRestantes = fechaProximaDosis.diff(hoy, 'day');
                
                // Si está dentro de los próximos 30 días
                if (diasRestantes >= 0 && diasRestantes <= 30) {
                  proximasActividades.push({
                    tipo: 'desparasitacion',
                    paciente: {
                      id: paciente.id,
                      nombre: paciente.nombre,
                      especie: paciente.especie
                    },
                    actividad: desparasitacionReciente.tipoDesparasitante || 'Desparasitación',
                    fechaProxima: desparasitacionReciente.proximaDosis,
                    diasRestantes: diasRestantes,
                    urgente: diasRestantes <= 7
                  });
                }
              }
            }
          } catch (error) {
            console.error(`Error procesando medicina preventiva para paciente ${paciente.nombre}:`, error);
          }
        }

        // Ordenar por días restantes (más urgente primero)
        proximasActividades.sort((a, b) => a.diasRestantes - b.diasRestantes);
        
        console.log(`ԣ� Encontradas ${proximasActividades.length} actividades de medicina preventiva pr+�ximas`);
        setProximasMedicinaPreventiva(proximasActividades.slice(0, 10)); // Mostrar m+�ximo 10
        
      } catch (error) {
        console.error('��� Error cargando pr+�ximas medicina preventiva:', error);
      }
    };

    fetchProximasMedicinaPreventiva();
  }, []);

  // Cargar datos financieros b+�sicos
  useEffect(() => {
    const fetchDatosFinancieros = async () => {
      try {
        setDatosFinancieros(prev => ({ ...prev, cargando: true }));
        
        // Obtener fecha del mes actual
        const inicioMes = dayjs().startOf('month');
        const finMes = dayjs().endOf('month');
        
        // Mismo cálculo que "Ingresos Totales" en Finanzas:
        // ventas de productos + ventas de petshop + servicios - adquisiciones (mes actual)
        const cargarColeccion = async (nombre) => {
          const snapshot = await getDocs(query(collection(db, nombre), orderBy('timestamp', 'desc')));
          return snapshot.docs.map(d => d.data());
        };
        const [ventas, ventasPetshop, servicios, adquisiciones] = await Promise.all([
          cargarColeccion('ventas'),
          cargarColeccion('ventasPetshop').catch(() => []),
          cargarColeccion('servicios'),
          cargarColeccion('adquisiciones').catch(() => [])
        ]);

        const enMesActual = (data) => {
          const fecha = dayjs(data.fecha);
          return fecha.isValid() && fecha.valueOf() >= inicioMes.valueOf() && fecha.valueOf() <= finMes.valueOf();
        };
        const sumar = (items, campo) =>
          items.filter(enMesActual).reduce((sum, item) => sum + (item[campo] || 0), 0);

        const ingresosMes =
          sumar(ventas, 'total') +
          sumar(ventasPetshop, 'total') +
          sumar(servicios, 'precio') -
          sumar(adquisiciones, 'costoTotal');
        
        // Actualizar estado
        setDatosFinancieros({
          ingresosMes,
          cargando: false
        });
        
        // Actualizar tarjeta de finanzas en stats
        setStats(prevStats => {
          const newStats = [...prevStats];
          newStats[2] = {
            ...newStats[2],
            value: new Intl.NumberFormat('es-CO', {
              style: 'currency',
              currency: 'COP',
              minimumFractionDigits: 0
            }).format(ingresosMes),
            change: 'Ver detalles'
          };
          return newStats;
        });
        
      } catch (error) {
        console.error('Error cargando datos financieros:', error);
        setDatosFinancieros({
          ingresosMes: 0,
          cargando: false
        });
      }
    };

    fetchDatosFinancieros();
  }, []);

  return (
    <AdminLayout title="Dashboard Administrativo" icon={DashboardIcon}>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom sx={{ color: '#602442', fontWeight: 'bold' }}>
          Bienvenido
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CalendarTodayIcon sx={{ color: '#c46494', mr: 1 }} />
          <Typography variant="subtitle1" sx={{ color: '#a55e88' }} gutterBottom>
            Resumen del día - {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Estad+�sticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  border: '1px solid #c46494', 
                  backgroundColor: 'white',
                  height: '100%', // Asegurar altura uniforme
                  cursor: index === 2 ? 'pointer' : 'default', // Cursor pointer solo para finanzas
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(196, 100, 148, 0.2)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
                onClick={index === 2 ? () => navigate('/admin/finanzas') : undefined}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        sx={{ 
                          color: '#a55e88', 
                          fontWeight: '500', 
                          fontSize: '1.1rem', 
                          display: 'flex', 
                          alignItems: 'center',
                          minHeight: '32px',
                          mb: 1
                        }} 
                      >
                        {stat.title}
                      </Typography>
                      <Typography 
                        variant={index === 2 ? "h4" : "h3"} 
                        component="div" 
                        sx={{ 
                          color: '#602442', 
                          fontWeight: 'bold', 
                          mb: 1,
                          fontSize: index === 2 ? '1.8rem' : undefined,
                          minHeight: '48px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Box sx={{ minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          label={stat.change} 
                          sx={{
                            backgroundColor: index === 0 ? '#c46494' : index === 1 ? '#4caf50' : '#ff9800',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: '50%', 
                      backgroundColor: '#fcdcf4',
                      border: '2px solid #c46494',
                      color: '#602442',
                      boxShadow: '0 4px 8px rgba(196, 100, 148, 0.15)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 60,
                      height: 60
                    }}>
                      {stat.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Pr+�ximas Citas */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              border: '1px solid #c46494', 
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(196, 100, 148, 0.15)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  sx={{
                    bgcolor: '#f8e7f1',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <EventAvailableIcon sx={{ color: '#c46494' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#602442', fontWeight: 'bold' }}>
                    Próximas Citas Pendientes
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a55e88' }}>
                    Hora actual: {dayjs().format('HH:mm')} - {citasPendientesHoy.length} {citasPendientesHoy.length === 1 ? 'cita pendiente próxima' : 'citas pendientes próximas'}
                  </Typography>
                </Box>
              </Box>
              
              {loading.appointments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} sx={{ color: '#c46494' }} />
                </Box>
              ) : error.appointments ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error al cargar citas: {error.appointments}
                </Alert>
              ) : citasProximas.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center'
                }}>
                  <Box 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      borderRadius: '50%',
                      p: 2,
                      mb: 2
                    }}
                  >
                    {citasHoy.length === 0 ? 
                      <EventAvailableIcon sx={{ fontSize: 40, color: '#9e9e9e' }} /> : 
                      <TodayIcon sx={{ fontSize: 40, color: '#9e9e9e' }} />
                    }
                  </Box>
                  <Typography variant="h6" sx={{ color: '#602442', mb: 1 }}>
                    No hay citas pendientes proximas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', maxWidth: '80%' }}>
                    No se encontraron citas pendientes futuras en el sistema. Puedes revisar la agenda completa.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ flexGrow: 1, mb: 2 }}>
                  {citasProximas.map((cita, index) => (
                    <ListItem 
                      key={cita.id || index} 
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: '#fcdcf4',
                          borderColor: '#c46494',
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: '#c46494',
                          border: '2px solid #f5f5f5',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <PetsIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              bgcolor: '#f0f7ff', 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1,
                              border: '1px solid #c5e1ff'
                            }}>
                              <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                {cita.horaMostrar || cita.horaNormalizada || cita.hora}
                              </Typography>
                            </Box>
                            <Chip
                              label={dayjs(cita.fechaNormalizada || cita.fecha).isValid() ? dayjs(cita.fechaNormalizada || cita.fecha).format('DD/MM/YYYY') : (cita.fecha || 'N/A')}
                              size="small"
                              sx={{
                                backgroundColor: '#fff3e0',
                                color: '#e65100',
                                fontWeight: 'bold',
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                            <Typography variant="body1" sx={{ color: '#602442', fontWeight: '500' }}>
                              {cita.paciente}
                            </Typography>
                            <Chip 
                              label={cita.tipo} 
                              size="small" 
                              sx={{ 
                                backgroundColor: '#c46494', 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}
                            />
                            {/* Indicador de tiempo restante */}
                            {(() => {
                              const fechaHoraCita = cita._fechaHora || dayjs(`${cita.fechaNormalizada || cita.fecha} ${cita.horaNormalizada || cita.hora}`, 'YYYY-MM-DD HH:mm');
                              if (!fechaHoraCita.isValid()) return null;

                              const minutosRestantes = fechaHoraCita.diff(dayjs(), 'minute');
                              if (minutosRestantes <= 30) {
                                return (
                                  <Chip 
                                    label={`${minutosRestantes} min`} 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: minutosRestantes <= 15 ? '#ff9800' : '#4caf50', 
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '0.65rem',
                                      height: 18
                                    }}
                                  />
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ color: '#a55e88' }}>
                              Dueno: {cita['due\u00f1o'] || cita.dueno || 'N/A'}
                            </Typography>
                            {cita.estado && (
                              <Chip 
                                label={cita.estado} 
                                size="small" 
                                sx={{ 
                                  mt: 0.5,
                                  backgroundColor: 
                                    cita.estado === 'urgente' ? '#f44336' : 
                                    cita.estado === 'confirmada' ? '#2196f3' : 
                                    cita.estado === 'programada' ? '#4caf50' : '#9e9e9e',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 20,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                sx={{ 
                  mt: 'auto',
                  borderColor: '#c46494',
                  color: '#602442',
                  '&:hover': {
                    backgroundColor: '#c46494',
                    color: 'white',
                    borderColor: '#c46494'
                  }
                }}
                onClick={() => navigate('/admin/agenda')}
                startIcon={<CalendarTodayIcon />}
              >
                Ver Agenda Completa
              </Button>
            </Paper>
          </Grid>
          
          {/* +�ltimos pacientes atendidos */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              border: '1px solid #c46494', 
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(196, 100, 148, 0.15)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  sx={{
                    bgcolor: '#e8f5e9',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#4caf50' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#602442', fontWeight: 'bold' }}>
                    Últimos Pacientes Atendidos
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Citas completadas hoy: {citasCompletadas.length}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="caption" sx={{ mb: 2, color: '#a55e88', display: 'block', textAlign: 'center' }}>
                Citas completadas hoy: {citasCompletadas.length}
              </Typography>
              
              {loading.recentPatients ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} sx={{ color: '#c46494' }} />
                </Box>
              ) : error.recentPatients ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error al cargar pacientes: {error.recentPatients}
                </Alert>
              ) : pacientesRecientes.length === 0 ? (
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                  bgcolor: '#f0f7ff',
                  borderRadius: 1,
                  mt: 2
                }}>
                  <Box sx={{ p: 2, mb: 1 }}>
                    <InfoIcon color="info" sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="body1" sx={{ mb: 1, color: '#1976d2', fontWeight: 'medium' }}>
                    No hay pacientes atendidos recientemente
                  </Typography>
                </Box>
              ) : (
                <List sx={{ flexGrow: 1, mb: 2 }}>
                  {pacientesRecientes.map((paciente, index) => (
                    <ListItem 
                      key={paciente.id || index} 
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        backgroundColor: '#fff',
                        '&:hover': {
                          backgroundColor: '#f0f8f1',
                          borderColor: '#4caf50',
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: paciente.especie === 'gato' ? '#2196f3' : paciente.especie === 'ave' ? '#ff9800' : '#4caf50', 
                          width: 40, 
                          height: 40,
                          border: '2px solid #f5f5f5',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <PetsIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ color: '#602442', fontWeight: '500' }}>
                              {paciente.nombre}
                            </Typography>
                            <Chip 
                              label={paciente.especie || 'No especificado'} 
                              size="small" 
                              sx={{ 
                                backgroundColor: paciente.especie === 'gato' ? '#2196f3' : paciente.especie === 'ave' ? '#ff9800' : '#4caf50', 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: '#a55e88' }}>
                              Dueño: {paciente.propietario}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                              Última visita: {formatearFecha(paciente.ultimaVisita) || formatearFecha(paciente.fechaCreacion) || 'No registrada'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                fullWidth
                sx={{ 
                  mt: 'auto',
                  py: 1.5,
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#4caf50',
                    color: 'white',
                    borderColor: '#4caf50'
                  }
                }}
                onClick={() => navigate('/admin/pacientes')}
                startIcon={<PetsIcon />}
              >
                Gestionar Pacientes
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Segunda fila de tarjetas */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Pr+�xima Medicina Preventiva */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              border: '1px solid #c46494', 
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(196, 100, 148, 0.15)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  sx={{
                    bgcolor: '#e3f2fd',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <VaccinesIcon sx={{ color: '#1976d2' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#602442', fontWeight: 'bold' }}>
                    Próxima Medicina Preventiva
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a55e88' }}>
                    Vacunas y desparasitaciones próximas: {proximasMedicinaPreventiva.length}
                  </Typography>
                </Box>
              </Box>
              
              {proximasMedicinaPreventiva.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center'
                }}>
                  <Box 
                    sx={{
                      bgcolor: '#e3f2fd',
                      borderRadius: '50%',
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <VaccinesIcon sx={{ color: '#1976d2', fontSize: '2rem' }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#a55e88', fontWeight: '500' }}>
                    No hay medicina preventiva programada
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                    Todos los pacientes están al día
                  </Typography>
                </Box>
              ) : (
                <List sx={{ flexGrow: 1, p: 0 }}>
                  {proximasMedicinaPreventiva.slice(0, 5).map((actividad, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: actividad.urgente ? '#ffebee' : '#e3f2fd',
                          border: `2px solid ${actividad.urgente ? '#f44336' : '#1976d2'}`
                        }}>
                          {actividad.tipo === 'vacuna' ? 
                            <VaccinesIcon sx={{ color: actividad.urgente ? '#f44336' : '#1976d2', fontSize: '1.2rem' }} /> :
                            <HealingIcon sx={{ color: actividad.urgente ? '#f44336' : '#1976d2', fontSize: '1.2rem' }} />
                          }
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: '#602442', fontWeight: '500' }}>
                              {actividad.paciente.nombre}
                            </Typography>
                            <Chip 
                              label={actividad.paciente.especie || 'No especificado'} 
                              size="small" 
                              sx={{ 
                                backgroundColor: actividad.paciente.especie === 'gato' ? '#2196f3' : 
                                               actividad.paciente.especie === 'ave' ? '#ff9800' : '#4caf50', 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: '500' }}>
                              {actividad.tipo === 'vacuna' ? '💉' : '🛡️'} {actividad.actividad}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                📅 {dayjs(actividad.fechaProxima).format('DD/MM/YYYY')}
                              </Typography>
                              <Chip 
                                label={actividad.diasRestantes === 0 ? 'HOY' : 
                                       actividad.diasRestantes === 1 ? 'MAÑANA' : 
                                       `${actividad.diasRestantes} días`}
                                size="small" 
                                sx={{ 
                                  backgroundColor: actividad.urgente ? '#f44336' : 
                                                 actividad.diasRestantes <= 3 ? '#ff9800' : '#4caf50',
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 18,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                sx={{ 
                  mt: 'auto',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderColor: '#1976d2'
                  }
                }}
                onClick={() => navigate('/admin/pacientes')}
                startIcon={<VaccinesIcon />}
              >
                Ver Medicina Preventiva
              </Button>
            </Paper>
          </Grid>

          {/* Espacio para otra tarjeta futura */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3, 
              border: '1px solid #c46494', 
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(196, 100, 148, 0.15)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  sx={{
                    bgcolor: '#fff3e0',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  <WarningIcon sx={{ color: '#ff9800' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#602442', fontWeight: 'bold' }}>
                    Productos Próximos a Vencer
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a55e88' }}>
                    Productos que vencen en menos de 30 días: {inventarioProximoAVencer.length}
                  </Typography>
                </Box>
              </Box>
              
              {loading.inventory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} sx={{ color: '#c46494' }} />
                </Box>
              ) : error.inventory ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error al cargar inventario: {error.inventory}
                </Alert>
              ) : inventarioProximoAVencer.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center'
                }}>
                  <Box 
                    sx={{
                      bgcolor: '#e8f5e9',
                      borderRadius: '50%',
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '2rem' }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: '500' }}>
                    Inventario en buen estado
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>
                    No hay productos próximos a vencer
                  </Typography>
                </Box>
              ) : (
                <List sx={{ flexGrow: 1, p: 0 }}>
                  {inventarioProximoAVencer.slice(0, 5).map((producto, index) => {
                    // Calcular días restantes
                    const calcularDiasRestantes = (fechaVencimiento) => {
                      if (!fechaVencimiento) return null;
                      
                      const hoy = dayjs();
                      let fechaVence;
                      
                      if (fechaVencimiento.toDate) {
                        fechaVence = dayjs(fechaVencimiento.toDate());
                      } else if (typeof fechaVencimiento === 'string' && fechaVencimiento.includes('/')) {
                        const partes = fechaVencimiento.split('/');
                        if (partes.length === 3) {
                          const dia = parseInt(partes[0], 10);
                          const mes = parseInt(partes[1], 10);
                          const anio = parseInt(partes[2], 10);
                          const fechaFormatoISO = `${anio}-${mes.toString().padStart(2, '0')}-${dia.toString().padStart(2, '0')}`;
                          fechaVence = dayjs(fechaFormatoISO);
                        }
                      } else {
                        fechaVence = dayjs(fechaVencimiento);
                      }
                      
                      return fechaVence.diff(hoy, 'day');
                    };

                    const diasRestantes = calcularDiasRestantes(producto.fechaVencimiento);
                    const urgente = diasRestantes <= 7;
                    const proximoAVencer = diasRestantes <= 15;

                    return (
                      <ListItem key={index} sx={{ px: 0, py: 1 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            bgcolor: urgente ? '#ffebee' : proximoAVencer ? '#fff3e0' : '#e8f5e9',
                            border: `2px solid ${urgente ? '#f44336' : proximoAVencer ? '#ff9800' : '#4caf50'}`
                          }}>
                            {urgente ? 
                              <WarningIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} /> :
                              proximoAVencer ?
                              <AccessTimeIcon sx={{ color: '#ff9800', fontSize: '1.2rem' }} /> :
                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                            }
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ color: '#602442', fontWeight: '500' }}>
                                {producto.nombre}
                              </Typography>
                              {producto.categoria && (
                                <Chip 
                                  label={producto.categoria} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: '#e3f2fd', 
                                    color: '#1976d2',
                                    fontWeight: 'bold',
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ color: '#666', fontWeight: '500' }}>
                                📦 Stock: {producto.cantidad || producto.stock || 0} unidades
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  📅 Vence: {
                                    producto.fechaVencimiento?.toDate ? 
                                    producto.fechaVencimiento.toDate().toLocaleDateString() :
                                    typeof producto.fechaVencimiento === 'string' ?
                                    producto.fechaVencimiento :
                                    'Fecha no válida'
                                  }
                                </Typography>
                                <Chip 
                                  label={
                                    diasRestantes === 0 ? 'VENCE HOY' : 
                                    diasRestantes === 1 ? 'VENCE MAÑANA' : 
                                    diasRestantes < 0 ? 'VENCIDO' :
                                    `${diasRestantes} días`
                                  }
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: 
                                      diasRestantes < 0 ? '#000' :
                                      urgente ? '#f44336' : 
                                      proximoAVencer ? '#ff9800' : '#4caf50',
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    height: 18,
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
              
              <Button 
                variant="outlined" 
                sx={{ 
                  mt: 'auto',
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': {
                    backgroundColor: '#ff9800',
                    color: 'white',
                    borderColor: '#ff9800'
                  }
                }}
                onClick={() => navigate('/admin/inventario')}
                startIcon={<InventoryIcon />}
              >
                Ver Inventario Completo
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </AdminLayout>
  );
};

export default DashboardAdmin;
